import {
  ChevronLeftRounded,
  ChevronRightRounded,
  Close,
} from '@mui/icons-material'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import { useEffect, useState } from 'react'
import { Loading } from 'router/Loading'
import { useAppSelector, useActions } from 'store/hooks'
import { Button } from 'ui'
import { useDebouncedCallback } from 'use-debounce'

import {
  OrderStepContent,
  OrderStepLayout,
} from '../CreateOrder/OrderStepLayout'
import { ProductOrderFooter } from '../CreateOrder/ProductOrderFooter'
import { ProductOrderHeader } from '../CreateOrder/ProductOrderHeader'
import { orderFormSpace } from '../namespaces/orderFormSpace'
import {
  useGetTimeSlotsAfterAddressQuery,
  useLazyCheckDealerCodeQuery,
} from '../redux/orders.api'
import { StepComponentProps } from '../types/StepComponentProps'

import { OrderDateDealerCode } from './OrderDateDealerCode'
import { OrderDateInputs } from './OrderDateInputs'
import { OrderNeedPay } from './OrderNeedPay'
import style from './OrderStepDate.module.scss'

// шаг 4
// выбор даты и времени
export function OrderStepDate({
  completeStep,
  handlePrevious,
}: StepComponentProps) {
  const [isButtonDisabled, setButtonDisabled] = useState<boolean>(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [isDealerReset, setIsDealerReset] = useState(false)

  const handleCompleteStep = () => completeStep(false)
  const handlePreviousStep = () => handlePrevious(false)
  const [
    checkDealerCode,
    { data: dealerData, isFetching, isUninitialized, originalArgs },
  ] = useLazyCheckDealerCodeQuery()
  const currentEditForm = useAppSelector(
    state => state.orderForm.currentEditForm,
  )
  const timeSlots = useAppSelector(
    state => state.orderForm.forms[currentEditForm].timeSlots,
  )
  const chosenAddress = useAppSelector(
    state => state.orderForm.forms[currentEditForm].address,
  )
  const dealerCode = useAppSelector(
    state => state.orderForm.forms[currentEditForm].dealerCode,
  )

  const { addTimeSlotOrder, removeTimeSlotOrder } = useActions()

  const { data, isLoading } = useGetTimeSlotsAfterAddressQuery(
    chosenAddress?.address || 'error',
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const debounceDealerChecking = useDebouncedCallback(() => {
    checkDealerCode(dealerCode)
  }, 300)

  // когда пришёл актуальный ответ — снимаем ручной сброс
  useEffect(() => {
    if (!isFetching && originalArgs === dealerCode) {
      setIsDealerReset(false)
    }
  }, [isFetching, originalArgs, dealerCode])

  useEffect(() => {
    // Поля даты/времени необязательные: пустой слот допустим,
    // частично заполненный — нет, полностью заполненный должен быть валидным
    const isTimeslotsValid = timeSlots.every(item => {
      const hasDate = !!item.date?.length
      const hasTime = !!item.value?.length
      if (!hasDate && !hasTime) return true
      return hasDate && hasTime && item.isValidDate
    })

    const isDataCurrent =
      !isFetching && !isDealerReset && originalArgs === dealerCode
    const isDealerDataValid = Boolean(
      (isDataCurrent && dealerData && Object.keys(dealerData)?.length) ||
        dealerCode.length === 0,
    )

    setButtonDisabled(!isTimeslotsValid || !isDealerDataValid)
  }, [
    timeSlots,
    dealerData,
    isUninitialized,
    isFetching,
    originalArgs,
    dealerCode,
    isDealerReset,
  ])

  useEffect(() => {
    if (isUninitialized || dealerData === undefined || isFetching) return
    if (originalArgs !== dealerCode) return

    if (
      (dealerData && !!Object.keys(dealerData).length) ||
      dealerCode.length === 0
    ) {
      setErrorText(null)
      return
    }

    setErrorText('Диллер не найден')
  }, [dealerData, isFetching, originalArgs, dealerCode])

  if (isLoading || !data) return <Loading />

  const getTitle = () => {
    switch (currentEditForm) {
      case orderFormSpace.FormType.new:
      case orderFormSpace.FormType.newCompany:
        return 'Дата и время замера'
      case orderFormSpace.FormType.service:
        return 'Дата и время диагностики'
      default:
        return 'error form'
    }
  }

  const getDescription = () => {
    switch (currentEditForm) {
      case orderFormSpace.FormType.new:
      case orderFormSpace.FormType.newCompany:
        return `Если требуется провести замер, выберите удобную дату и время. 
        Если замер не требуется, перейдите далее.`
      case orderFormSpace.FormType.service:
        return `Выберите удобные даты и время, чтобы провести 
        диагностику на вашем объекте`
      default:
        return 'error form'
    }
  }

  const dealerName =
    !isFetching &&
    !isUninitialized &&
    !isDealerReset &&
    originalArgs === dealerCode
      ? dealerData?.name
      : undefined

  return (
    <OrderStepLayout>
      <ProductOrderHeader
        step='Шаг 4'
        title={getTitle()}
        description={getDescription()}
      />
      <OrderNeedPay />
      <OrderStepContent>
        <div className={style.formWrapper}>
          <div className={style.orderDateInputs}>
            {timeSlots.map(item => (
              <div className={style.orderDateInputsWrapper} key={item.key}>
                <OrderDateInputs item={item} data={data} />
                {timeSlots.length > 1 && (
                  <Button
                    startIcon={<Close />}
                    onClick={() => removeTimeSlotOrder(item.key)}
                    className={style.removeDateInputs}
                    data-testid={`order-remove-date-${item.key}`}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            ))}
          </div>

          <Button
            startIcon={<AddCircleRoundedIcon />}
            className={style.addDateInputs}
            onClick={() => {
              addTimeSlotOrder()
            }}
            data-testid='order-add-date'
          >
            Выбрать дополнительные дату и время
          </Button>

          <OrderDateDealerCode
            setButtonDisabled={setButtonDisabled}
            debounceDealerChecking={debounceDealerChecking}
            dealerDataLoading={isFetching}
            errorText={errorText}
            setErrorText={setErrorText}
            dealerName={dealerName}
            onResetDealer={() => setIsDealerReset(true)}
          />
        </div>
      </OrderStepContent>
      <ProductOrderFooter
        nextButton={{
          onClick: handleCompleteStep,
          Icon: <ChevronRightRounded />,
          text: 'Далее',
          disabled: isButtonDisabled,
        }}
        prevButton={{
          onClick: handlePreviousStep,
          Icon: <ChevronLeftRounded />,
          text: 'Предыдущий шаг',
        }}
      />
    </OrderStepLayout>
  )
}
